import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';

import PageNotFound from '../page-not-found';
import { StaticProfileConfig } from '@lemontaps/lib/types/api';

import { useHistory } from 'react-router-dom';
import useQuery from '../../../infrastructure/hooks/useQuery';
import { fetchProfile as fetchProfile_api } from '@/infrastructure/apis/profile';
import StaticProfileSkeleton from '@/components/Profile/components/StaticProfileSkeleton';
import MetaTags from './components/MetaTags';
import useDeferredLoader from '@/infrastructure/hooks/useDeferredLoader';
import { I18nextProvider } from 'react-i18next';
import { I18N_LOCAL_STORAGE_KEY, QUERY_KEY_LANG, getNewI18nInstance } from '@/config/i18n';
import { i18n } from 'i18next';
import { base64UrlDecode } from '@lemontaps/lib/util';
import StaticProfileUI from '@/components/Profile/StaticProfile';
import useDeviceInfo from '@/infrastructure/hooks/useDeviceInfo';
import { useFavicon } from '@/application/hooks';
import { routePaths } from '@/infrastructure/constants';
import MaintenanceScreen from '../maintenance';

type RouteType = Record<string, string | Record<string, string>>;

const flattenRoutes = (routes: RouteType): string[] => {
  return Object.values(routes)
    .map((x: string | RouteType) => (typeof x === 'object' ? flattenRoutes(x) : x))
    .flat();
};

const isKnownRoute = (path: string) => {
  const allRoutes = flattenRoutes(routePaths as unknown as RouteType).filter(
    route => route !== '/',
  );
  return allRoutes.some(route => path.startsWith(route));
};

// TODO: profile page optimizations
// consolidate logic, universalize logic, make parts re-usable

// MAINTENANCE MODE: Authentication and contact form are disabled

type TParams = {
  username: string;
};

export default function UserProfile(props: RouteComponentProps<TParams>): JSX.Element {
  const usernameOrCode = props.match.params.username;

  const query = useQuery();
  const history = useHistory();
  const location = useLocation();

  const forceProfile = !!query.get('forceProfile');

  const { isSafari, isIOS } = useDeviceInfo();

  // always start the user on top of the page
  useEffect(() => window.scrollTo(0, 0), []);

  // i18n instance logic
  const [i18nProfile, setI18nProfile] = useState<null | i18n>(null);

  // initiate language to fetch profile for
  const [activeLanguage, setActiveLanguage] = useState(
    query.get(QUERY_KEY_LANG)?.substring(0, 2) ||
      localStorage.getItem(I18N_LOCAL_STORAGE_KEY.PROFILE) ||
      navigator.language,
  );

  // used to initiate the i18n instance once the profile information has been fetched
  const initI18nProfile = useCallback(
    (profile: StaticProfileConfig) => {
      if (!i18nProfile) {
        setI18nProfile(
          getNewI18nInstance({
            fallbackLng: 'en',
            supportedLngs: profile.meta.supportedLangs,
            lng: profile.meta.lang,
            detection: {
              order: ['localStorage', 'navigator'],
              lookupLocalStorage: I18N_LOCAL_STORAGE_KEY.PROFILE,
            },
          }),
        );
      }
    },
    [i18nProfile],
  );

  // query params to forward to the server when fetching the profile
  const paramsFromQuery = useMemo(
    () => ({
      uid: query.get('uid')
        ? parseInt(query.get('uid') || '') ||
          parseInt(base64UrlDecode(query.get('uid') || '')) ||
          undefined
        : undefined,
    }),
    [query],
  );

  // profile fetching
  const [profile, setProfile] = useState<StaticProfileConfig | null>(null);
  const cachedProfiles = useRef<Record<string, StaticProfileConfig>>({}); // if lang is switched back and forth multiple times
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileReloading, setProfileReloading] = useState(false);
  const fetchProfile = useCallback(
    (setLoaderFct = setProfileLoading) => {
      if (cachedProfiles.current[activeLanguage]) {
        setProfile(cachedProfiles.current[activeLanguage]);
        return;
      }

      setLoaderFct(true);
      fetchProfile_api(usernameOrCode, activeLanguage, paramsFromQuery)
        .then(res => {
          const { status, name } = res;
          if (status === 200 && name === 'profile') {
            const profile = res.profile;
            if (profile.forward?.url && !forceProfile) {
              if (profile.forward?.isVCard) {
                setProfile(profile);
                initI18nProfile(profile);
                setLoaderFct(false);
              }
              if (isIOS || isSafari) {
                // TODO: currently a workaround
                // should be updated -> we need to wait until assets are loaded, and only then trigger this. That's because on Safari (and other browsers on iPhones), the loading of assets will be blocked once the download is initiated
                // so we need to collect the resources needed (maybe via a dedicated 'resources' property in the StaticProfileConfig) react to their onload event and only then initiate a download here
                setTimeout(() => {
                  if (profile.forward?.url) window.location.replace(profile.forward.url);
                }, 1500); // https://chatgpt.com/share/675c1e50-5284-8010-a5e6-298a51797787
              } else {
                window.location.replace(profile.forward.url);
              }
            } else if (profile.meta.domain !== window.location.origin) {
              window.location.replace(
                `${profile.meta.domain}/${profile.meta.username}` + window.location.search,
              );
            } else {
              cachedProfiles.current[activeLanguage] = profile;
              setProfile(profile);
              initI18nProfile(profile);
              setLoaderFct(false);
            }
          } else if (status === 200 && name === 'code') {
            const code = res.code;
            history.push(`/landing/check/${code}`);
          }
        })
        .catch(() => setLoaderFct(false));
    },
    [
      activeLanguage,
      forceProfile,
      history,
      initI18nProfile,
      isSafari,
      isIOS,
      paramsFromQuery,
      usernameOrCode,
    ],
  );
  useEffect(() => {
    if (!profile) {
      // don't refetch when forwarded due to code
      fetchProfile();
    }
  }, [fetchProfile, profile]);
  // do re-fetch on language change
  const prevLang = useRef<string>(activeLanguage);
  useEffect(() => {
    if (prevLang.current && activeLanguage !== prevLang.current) {
      prevLang.current = activeLanguage;
      fetchProfile(setProfileReloading);
    }
  }, [activeLanguage, fetchProfile]);

  useDeferredLoader(profileReloading, 'profile-reloader');

  // once profile is available, attach listener to lang changed event
  // to update the state "activeLanguage". Otherwise no re-render would be triggered
  useEffect(() => {
    if (i18nProfile) {
      const listener = (lang: string) => {
        setActiveLanguage(lang);
        if (query.get(QUERY_KEY_LANG)) {
          query.set(QUERY_KEY_LANG, lang);
          history.replace('/' + (profile?.meta.username || '') + '?' + query.toString());
        }
      };
      i18nProfile.on('languageChanged', listener);
      return () => i18nProfile.off('languageChanged', listener);
    }
  }, [history, i18nProfile, profile?.meta.username, query]);

  // replace path with username if fetched from code
  useEffect(() => {
    if (profile && profile?.meta.username !== usernameOrCode) {
      history.replace('/' + profile.meta.username + window.location.search);
    }
  }, [history, profile, usernameOrCode]);

  // determine the favicon to render
  useFavicon({
    renderIcon: !!profile?.config,
    iconUrl: profile?.config.iconUrl,
  });

  // MAINTENANCE MODE: Simplified - no auth-based rendering decisions, no analytics
  const renderTryForFree = profile?.config.tryForFreeBanner;

  if (profileLoading) {
    return <StaticProfileSkeleton />;
  } else if (!profile || !i18nProfile) {
    if (isKnownRoute(location.pathname)) {
      return <MaintenanceScreen />;
    }
    return <PageNotFound />;
  }

  // MAINTENANCE MODE: Contact form and analytics are disabled
  // No BottomAuthBar since authentication is disabled
  return (
    <I18nextProvider i18n={i18nProfile}>
      <MetaTags bio={profile.bio} />

      <StaticProfileUI
        profile={profile}
        renderTryForFreeBanner={renderTryForFree}
        renderCookieSettings={profile.footer.cookieButton}
        suppressLeadGen={true}
      />
    </I18nextProvider>
  );
}
